<script setup lang="ts">
import { renderMarkdown } from "@/lib/markdown";
import { computed, ref } from "vue";

import type { MockQuestion, MockSubmitResponse } from "@/api/types";
import ResponseFeedback from "@/components/feedback/ResponseFeedback.vue";
import AttainmentRing from "@/components/ui/AttainmentRing.vue";
import RlCard from "@/components/ui/RlCard.vue";
import RlPill from "@/components/ui/RlPill.vue";
import {
  SECURE_FLOOR_PCT,
  STRONG_FLOOR_PCT,
  type AttainmentTier,
} from "@/lib/attainment";

/**
 * Mock paper results (PRD §6.8 PE-8.7, design handoff §5 screen 8).
 *
 * A result is information, not a verdict: no confetti or congratulation on a
 * high score, no "you dropped N marks" framing on a low one. The score, the
 * marks, and what each question earned — the student draws their own
 * conclusion.
 */

const props = withDefaults(
  defineProps<{
    result: MockSubmitResponse;
    /**
     * The paper that produced `result`, used only to name the topic on each
     * breakdown row — `MockResultItem` carries marks and feedback but no topic.
     * Optional so the results render (numbered, untopiced) without it.
     */
    questions?: MockQuestion[];
  }>(),
  { questions: () => [] },
);

defineEmits<{ close: []; again: [] }>();

/**
 * The ring is the knowledge ring, so its colour has to mean what it means
 * everywhere else — hence the shared attainment floors rather than a private
 * palette for mocks. `needs_review` and `not_started` cannot apply: a freshly
 * marked paper is neither stale nor unattempted.
 */
const tier = computed<AttainmentTier>(() => {
  if (props.result.overall_pct >= SECURE_FLOOR_PCT) return "secure";
  if (props.result.overall_pct >= STRONG_FLOOR_PCT) return "strong";
  return "learning";
});

const topicById = computed(() => {
  const map = new Map<string, string>();
  for (const q of props.questions) map.set(q.interaction_id, q.topic);
  return map;
});

const rows = computed(() =>
  props.result.results.map((item, i) => ({
    item,
    n: i + 1,
    topic: topicById.value.get(item.interaction_id) ?? "",
    pct:
      item.marks_available > 0
        ? Math.min(
            100,
            Math.max(0, (item.marks_awarded / item.marks_available) * 100),
          )
        : 0,
  })),
);

/** One open at a time: the breakdown stays scannable at 20 questions. */
const openId = ref<string | null>(null);

function toggle(interactionId: string): void {
  openId.value = openId.value === interactionId ? null : interactionId;
}

function renderFeedback(text: string): string {
  return renderMarkdown(text);
}
</script>

<template>
  <RlCard>
    <div class="results rl-anim-fade-up">
      <RlPill tone="warn">Mock complete</RlPill>

      <div class="score">
        <AttainmentRing :pct="result.overall_pct" :tier="tier" :size="96" />
        <div class="score-text">
          <div class="score-pct">{{ Math.round(result.overall_pct) }}%</div>
          <div class="score-marks">
            {{ result.total_marks_awarded }} /
            {{ result.total_marks_available }} marks
          </div>
        </div>
      </div>

      <div class="breakdown">
        <div
          v-for="row in rows"
          :key="row.item.interaction_id"
          class="row-wrap"
        >
          <button
            type="button"
            class="q-row"
            :aria-expanded="openId === row.item.interaction_id"
            @click="toggle(row.item.interaction_id)"
          >
            <span class="row-n">Q{{ row.n }}</span>
            <span class="row-topic">{{
              row.topic || `Question ${row.n}`
            }}</span>
            <span class="row-bar" aria-hidden="true">
              <span class="row-fill" :style="{ width: `${row.pct}%` }" />
            </span>
            <span class="row-score">
              {{ row.item.marks_awarded }}/{{ row.item.marks_available }}
            </span>
          </button>

          <div v-if="openId === row.item.interaction_id" class="row-detail">
            <!-- Marker feedback is markdown from our own grading backend,
                 rendered the same way practice marking renders it. -->
            <div class="feedback" v-html="renderFeedback(row.item.feedback)" />
            <ResponseFeedback :interaction-id="row.item.interaction_id" />
          </div>
        </div>
      </div>

      <div class="actions">
        <button type="button" class="secondary" @click="$emit('close')">
          Close
        </button>
        <button type="button" class="primary" @click="$emit('again')">
          Another session
        </button>
      </div>
    </div>
  </RlCard>
</template>

<style scoped>
.results {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
  align-items: flex-start;
  width: 100%;
  max-width: 560px;
}

/* ── Headline ────────────────────────────────────────────── */
.score {
  display: flex;
  align-items: center;
  gap: var(--sp-5);
}

.score-pct {
  font-size: 46px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--rl-fg-1);
}

.score-marks {
  margin-top: var(--sp-2);
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-md);
  color: var(--rl-fg-3);
}

/* ── Per-question breakdown ──────────────────────────────── */
.breakdown {
  width: 100%;
  border: 1px solid var(--rl-border-subtle);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.row-wrap + .row-wrap {
  border-top: 1px solid var(--rl-border-subtle);
}

.q-row {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  width: 100%;
  padding: var(--sp-3) var(--sp-4);
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  text-align: left;
  cursor: pointer;
}

.q-row:hover {
  background: var(--rl-bg-subtle);
}

.row-n {
  width: 26px;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.row-topic {
  flex: 1;
  overflow: hidden;
  font-size: var(--rl-text-md);
  color: var(--rl-fg-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-bar {
  flex: none;
  width: 70px;
  height: 6px;
  overflow: hidden;
  background: var(--rl-bg-muted);
  border-radius: var(--radius-pill);
}

.row-fill {
  display: block;
  height: 100%;
  background: var(--rl-green-600);
  border-radius: var(--radius-pill);
}

.row-score {
  width: 44px;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-sm);
  text-align: right;
  color: var(--rl-fg-1);
}

.row-detail {
  padding: 0 var(--sp-4) var(--sp-4) calc(var(--sp-4) + 26px + var(--sp-3));
}

.feedback {
  margin-bottom: var(--sp-2);
  font-size: var(--rl-text-md);
  line-height: 1.55;
  color: var(--rl-fg-2);
}

.feedback :deep(p) {
  margin: 0 0 var(--sp-2);
}

.feedback :deep(p:last-child) {
  margin-bottom: 0;
}

/* ── Actions ─────────────────────────────────────────────── */
.actions {
  display: flex;
  gap: var(--sp-2);
  width: 100%;
}

.actions button {
  flex: 1;
  justify-content: center;
}
</style>
